/**
 * CropCatalogPanel.tsx — CRUD del catálogo de cultivos del tenant (pestaña "Catálogo"
 * de Cultivo). Separa el mantenimiento del catálogo de la operación diaria de asignar
 * cultivo a una zona. Borrar pide confirmación y, si el cultivo está en uso, muestra
 * el 409 `error:crop_in_use` con los conteos.
 */
import { useFieldErrors } from '../../hooks/useFieldErrors';
import { useState } from 'react';
import {
  makeStyles, tokens, Text, Button, Badge, Input, Spinner, Field, Combobox, Option,
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
} from '@fluentui/react-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useT } from '../../i18n';
import { fetchCrops, fetchFsmaCoverage, createCrop, updateCrop, deleteCrop, type Crop, type FsmaCoverageStatus } from '../../api/terrain.api';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  error: { color: tokens.colorStatusDangerForeground1 },
  swatch: { display: 'inline-block', width: '14px', height: '14px', borderRadius: '50%', verticalAlign: 'middle' },
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
});

export function CropCatalogPanel({ canWrite }: { canWrite: boolean }) {
  const t = useT();
  const s = useStyles();
  const qc = useQueryClient();

  const { data: crops = [], isLoading } = useQuery({ queryKey: ['crops-catalog'], queryFn: fetchCrops });
  const { data: coverage = [] } = useQuery({ queryKey: ['fsma-coverage'], queryFn: fetchFsmaCoverage });
  const coverageByCrop = new Map(coverage.map(c => [c.cropId, c.status as FsmaCoverageStatus]));
  const coverageLabel: Record<FsmaCoverageStatus, string> = {
    COVERED: t.crop.fsmaCovered, NOT_COVERED: t.crop.fsmaNotCovered, UNKNOWN: t.crop.fsmaUnknown,
  };

  // null = cerrado; {} = alta; {cropId} = edición
  const [editing, setEditing] = useState<{ cropId?: string } | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [typeText, setTypeText] = useState('');
  const fe = useFieldErrors();
  const [toDelete, setToDelete] = useState<Crop | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const typeOptions = [
    { value: 'HOJAS', label: t.crop.cropTypeLeaves },
    { value: 'FRUTAS', label: t.crop.cropTypeFruit },
    { value: 'OTRO', label: t.crop.cropTypeOther },
  ];
  const typeLabel = (v: string) => typeOptions.find(o => o.value === v)?.label ?? v;
  const typedUpper = typeText.trim().toUpperCase();
  const typedIsOption = typeOptions.some(o => o.label.toUpperCase() === typedUpper || o.value === typedUpper);

  const openForm = (c?: Crop) => {
    setEditing(c ? { cropId: c.cropId } : {});
    setName(c?.name ?? '');
    setType(c?.cropType ?? '');
    setTypeText(c?.cropType ? typeLabel(c.cropType) : '');
    setMsg(null); fe.clear();
  };
  const closeForm = () => { setEditing(null); setName(''); setType(''); setTypeText(''); fe.clear(); };

  const saveMut = useMutation({
    mutationFn: () => editing?.cropId
      ? updateCrop(editing.cropId, { name: name.trim(), cropType: type || undefined })
      : createCrop({ name: name.trim(), cropType: type || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['crops-catalog'] }); closeForm(); },
    onError: () => setMsg(t.crop.catalogSaveError),
  });

  const deleteMut = useMutation({
    mutationFn: (c: Crop) => deleteCrop(c.cropId),
    onSuccess: () => { setMsg(null); setToDelete(null); qc.invalidateQueries({ queryKey: ['crops-catalog'] }); },
    onError: (e: any, c) => {
      setToDelete(null);
      const data = e?.response?.data;
      if (e?.response?.status === 409 && data?.code === 'error:crop_in_use') {
        const u = data.usage ?? {};
        const parts = [
          u.lotes > 0 && t.crop.usageLotes.replace('{n}', String(u.lotes)),
          u.modules > 0 && t.crop.usageModules.replace('{n}', String(u.modules)),
          u.history > 0 && t.crop.usageHistory.replace('{n}', String(u.history)),
          u.phenology > 0 && t.crop.usagePhenology.replace('{n}', String(u.phenology)),
        ].filter(Boolean).join(', ');
        setMsg(t.crop.assignDeleteInUse.replace('{name}', c.name).replace('{usage}', parts));
      } else {
        setMsg(t.crop.assignDeleteError);
      }
    },
  });

  return (
    <div className={s.root}>
      <div className={s.toolbar}>
        <Text weight="semibold">{t.crop.catalogTitle}</Text>
        {canWrite && <Button appearance="primary" onClick={() => openForm()}>{t.crop.catalogNew}</Button>}
      </div>
      {msg && <Text size={200} className={s.error} role="alert">{msg}</Text>}

      {isLoading ? <Spinner size="tiny" /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{t.crop.catalogColName}</TableHeaderCell>
              <TableHeaderCell>{t.crop.assignCropType}</TableHeaderCell>
              <TableHeaderCell>{t.crop.catalogColColor}</TableHeaderCell>
              {canWrite && <TableHeaderCell>{t.common.actions}</TableHeaderCell>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {crops.length === 0 && (
              <TableRow><TableCell colSpan={canWrite ? 4 : 3}>{t.crop.catalogEmpty}</TableCell></TableRow>
            )}
            {crops.map(c => (
              <TableRow key={c.cropId}>
                <TableCell>
                  {c.name}{' '}
                  {coverageByCrop.get(c.cropId) && (
                    <Badge size="small" appearance="outline" data-testid="fsma-badge">
                      {coverageLabel[coverageByCrop.get(c.cropId)!]}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>{c.cropType ? typeLabel(c.cropType) : '—'}</TableCell>
                <TableCell>
                  <span className={s.swatch} style={{ background: c.colorHex }} aria-hidden />
                </TableCell>
                {canWrite && (
                  <TableCell>
                    <Button size="small" appearance="subtle" aria-label={`${t.crop.catalogEdit} ${c.name}`} onClick={() => openForm(c)}>
                      {t.crop.catalogEdit}
                    </Button>
                    <Button size="small" appearance="subtle" aria-label={`${t.crop.assignDeleteCrop} ${c.name}`} onClick={() => setToDelete(c)}>
                      {t.common.delete}
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={editing !== null} onOpenChange={(_, d) => { if (!d.open) closeForm(); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{editing?.cropId ? t.crop.catalogEditTitle : t.crop.catalogCreateTitle}</DialogTitle>
            <DialogContent className={s.form}>
              <Field label={t.crop.catalogColName} required {...fe.props('name')}>
                <Input value={name} onChange={(_, d) => { setName(d.value); fe.clear('name'); }} />
              </Field>
              <Field label={t.crop.assignCropType}>
                <Combobox
                  freeform
                  maxLength={40}
                  value={typeText}
                  selectedOptions={type ? [type] : []}
                  onChange={(e) => { setTypeText(e.target.value); setType(e.target.value.trim().toUpperCase()); }}
                  onOptionSelect={(_, d) => { /* Con `freeform`, Fluent también avisa al teclear (sin opción): se ignora. */ if (d.optionValue === undefined) return; setType(d.optionValue ?? ''); setTypeText(d.optionText ?? ''); }}
                  onBlur={() => {
                    const match = typeOptions.find(o => o.label.toUpperCase() === typedUpper || o.value === typedUpper);
                    if (match) { setType(match.value); setTypeText(match.label); }
                  }}
                >
                  {typeOptions.map(o => <Option key={o.value} value={o.value} text={o.label}>{o.label}</Option>)}
                  {typedUpper && !typedIsOption && (
                    <Option value={typedUpper} text={typedUpper}>{t.crop.cropTypeAdd.replace('{text}', typeText.trim())}</Option>
                  )}
                </Combobox>
              </Field>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={closeForm}>{t.common.cancel}</Button>
              <Button appearance="primary" disabled={saveMut.isPending} onClick={() => { if (fe.validate({ name: !name.trim() && t.common.validation.nameRequired })) saveMut.mutate(); }}>
                {t.common.save}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={toDelete !== null} onOpenChange={(_, d) => { if (!d.open) setToDelete(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t.crop.assignDeleteCrop}</DialogTitle>
            <DialogContent>{t.crop.catalogDeleteConfirm.replace('{name}', toDelete?.name ?? '')}</DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setToDelete(null)}>{t.common.cancel}</Button>
              <Button appearance="primary" disabled={deleteMut.isPending} onClick={() => toDelete && deleteMut.mutate(toDelete)}>
                {t.common.delete}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
